import React from 'react';
import { useLocation, useHistory, Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  getSimpleRemoteConfig,
  IEmployerContent,
} from '../../../utils/remote-config-manager';
import { logout } from '../../../utils/auth-manager';
import {
  Drawer,
  DrawerItemProps,
  DrawerItem,
} from '@progress/kendo-react-layout';
import { colors } from '../../../style';
import {
  prefixObjectValues,
  showPaymentPageBasedOnEmployerId,
  enums,
} from '../../../utils';
import { isDueTask } from '../../tasks/utils';
import {
  DashboardMenuItem,
  OpenSansSemiBoldLabel,
  OpenSansRegularValue,
  StatusBubble,
  StyledNotification,
  ToastNotificationGroupStyle,
} from '../simple-styled-components/index';
import {
  getContent,
  getHtmlFromMarkdown,
  getReminderData,
} from '../../../utils/index';
import { convertAnchorTagsToRouterLinks } from '../../../utils/html-parsing-helpers';
import Button from '../button';
import { ButtonTypes, AppPaths } from '../../../utils/enums';
import {
  AccountMenu,
  AccountMenuProps,
} from '../app-header/account-menu/index';
import { LogoArea } from '../app-header/logo-area/index';
import MobileMenuIcon from '../../../images/mobile-menu.svg';
import Handlebars from 'handlebars';
import { registerSelf } from '../../../hooks/use-handlebars/helpers';
import { PersonDataContext } from '../../../contexts/person-data-context';
import { PersonData } from '../../../data-models';
import {
  IKenticoData,
  KenticoDataContext,
} from '../../../contexts/kentico-data-context';
import { PreferencesContext } from '../../../contexts/preferences-context';
import { EmployerEducationalContent } from '../../../kontent-models';
import { useQuery } from '@tanstack/react-query';
import {
  MobileReminderMenu,
  MobileReminderMenuComponent,
  ReminderMenuItem,
} from '../app-header/reminder-centre';
import { useTasksGet } from '../../../hooks/use-data';
import { Spinner } from '../spinner';

export interface DashboardMenuEntry {
  text: string;
  id: string;
  active?: boolean;
  action: () => void;
}
export interface DashboardMenuProps {
  keyId?: string;
}

export interface MenuItem {
  text?: string;
  id?: string;
  mobileonly?: string;
  route: string;
  bold?: string;
  separator?: boolean;
  action?: () => void;
}

export interface MenuItemProps {
  active?: number;
}

export interface AppHeaderProps {
  keyId?: string;
  accountMenuItems?: AccountMenuProps['menuItems'];
  firstName?: string;
  lastName?: string;
  leftalign?: boolean;
}

const paymentPrefs = 'Payment Preferences';
const logoutTimeout = 10000;
const logoutError = 'We couldnt complete sign out. Please try again.';

const DrawerLink = styled(Link)`
  text-decoration: none;
  width: 100%;
`;

const CustomDrawerItem = (props: DrawerItemProps) => {
  const external = props.id === 'account-settings';
  const { route, action, ...others } = props;
  let drawerRoute = route;
  let drawerAction;

  if (external) {
    drawerRoute = '';
    drawerAction = route;
  }

  const handleLogout = (event: React.MouseEvent) => {
    if (props.id === 'log-out') {
      event.preventDefault();
      (props as MenuItem).action?.();
    }
  };

  return (
    <DrawerItem {...others}>
      {props.bold === 'Y' ? (
        <DrawerLink
          to={route}
          onClick={props.id === 'log-out' ? handleLogout : undefined}
        >
          <OpenSansSemiBoldLabel>{props.text}</OpenSansSemiBoldLabel>
        </DrawerLink>
      ) : (
        <DrawerLink to={drawerRoute} onClick={drawerAction}>
          <OpenSansRegularValue>{props.text}</OpenSansRegularValue>
        </DrawerLink>
      )}
    </DrawerItem>
  );
};

const getMenuItems = (
  accountSettingsLink: string,
  showPaymentPrefs: boolean,
  hasDueTasks: number,
  showPaymentInfo: boolean,
  showEducationTab: boolean,
  educationTabName: string,
  handleLogout: () => void,
) => {
  let menuItems = [
    {
      text: 'Start a leave or claim',
      id: 'startClaim',
      bold: 'Y',
      mobileonly: 'Y',
      route: AppPaths.startClaim,
    },
    {
      separator: true,
      mobileonly: 'Y',
      route: '/',
    },
    {
      text: 'Home',
      id: 'summary',
      mobileonly: 'N',
      route: AppPaths.appRoot,
    },
    {
      text: 'Calendar',
      id: 'absences',
      mobileonly: 'N',
      route: AppPaths.absenceHistoryAll,
    },
    {
      text: 'Tasks',
      id: 'tasks',
      mobileonly: 'N',
      route: AppPaths.tasks,
      statusbubble: hasDueTasks,
    },
    {
      text: 'Payments',
      id: 'payments',
      mobileonly: 'N',
      route: AppPaths.payments,
    },
    {
      text: 'Documents',
      id: 'documents',
      mobileonly: 'N',
      route: AppPaths.documents,
    },
    {
      text: 'Upload',
      id: 'new-upload',
      mobileonly: 'N',
      route: AppPaths.newUpload,
    },
    {
      text: 'Available Time',
      id: 'time',
      mobileonly: 'N',
      route: AppPaths.time,
    },
    {
      text: educationTabName,
      id: 'learning-hub-tab',
      mobileonly: 'N',
      route: AppPaths.learninghub,
    },
    {
      separator: true,
      mobileonly: 'Y',
      route: '/',
    },
    {
      text: 'Account Settings',
      id: 'account-settings',
      mobileonly: 'Y',
      route: () => globalThis.location.assign(accountSettingsLink),
    },
    {
      text: 'Notification Preferences',
      id: 'notification-preferences',
      mobileonly: 'Y',
      route: AppPaths.profile,
    },
    {
      text: paymentPrefs,
      id: 'payment-preferences',
      mobileonly: 'Y',
      route: AppPaths.profile,
    },
    {
      separator: true,
      mobileonly: 'Y',
      route: '/',
    },
    {
      text: 'Log out',
      id: 'log-out',
      bold: 'Y',
      mobileonly: 'Y',
      route: '',
      action: handleLogout,
    },
  ];

  if (!showPaymentPrefs) {
    menuItems = menuItems.filter((m) => m.id !== 'payment-preferences');
  }

  if (!showPaymentInfo) {
    menuItems = menuItems.filter((m) => m.id !== 'payments');
  }

  if (!showEducationTab) {
    menuItems = menuItems.filter((m) => m.id !== 'learning-hub-tab');
  }

  return menuItems;
};

/*******************************************************************************
 * Adds a unique prefix to the domIds to create a unique id
 * @param {string} prefix A unique prefix to add to the domIds
 * @returns {domIdsStatic} If a prefix is provided, then a copy of domIdsStatic
 * is returned with the property values modified to include the prefix. If a
 * prefix is not provided a copy of domIdsStatic is returned.
 ******************************************************************************/
const domIdsUnique = (prefix?: string) =>
  prefixObjectValues(prefix, domIdsStatic);

/*******************************************************************************
 * DOM identifier template for this component
 ******************************************************************************/
const domIdsStatic = {
  rootNode: 'menu-container',
  newClaimBtn: 'new-claim-button',
  menuItem: 'menu-item-',
};

/*******************************************************************************
 * styles / styled components
 ******************************************************************************/

const Container = styled.div`
  padding: 2rem 3rem 0.75rem;
  width: 100%;

  @media (max-width: 1024px) {
    padding: 2rem 1.5rem 1rem;
  }
  @media (max-width: 768px) {
    padding: 2rem 1rem 1rem;
  }
  @media (max-width: 394px) {
    .tw-itemns-center {
      justify-content: center !important;
      flex-align: center !important;
    }

    #new-claim-button {
      margin-left: auto !important;
      margin-right: auto !important;
      display: block !important;
      justify-self: center !important;
      grid-column: span 12 / span 12 !important;
    }
  }
`;

const DashboardMenuItemMod = styled(DashboardMenuItem)`
  margin-top: 37px;
  text-decoration: none;
`;

const ItemText = styled.span<MenuItemProps>`
  text-decoration: none;
  padding-bottom: 8px;
  border-bottom: ${(props) =>
    props.active === 1 ? '3px solid #0199A6' : 'none'};

  &:hover {
    border-bottom: 3px solid ${colors.poolD1};
    color: ${colors.warmNeutralL3};
    text-decoration: none;
  }

  &:visited {
    color: ${colors.warmNeutralL3};
  }
`;

const MobileMenuButton = styled.button`
  background-color: ${colors.coolNeutralL1};
  border: 0;
  cursor: pointer;
  display: none;
  outline: none;
  padding: 5px;

  @media (max-width: 1024px) {
    display: block;
  }
`;

const ButtonContainer = styled.div`
  border: 0;
  cursor: pointer;
  display: none;
  outline: none;
  padding: 5px;
  margin-left: 12px;

  @media (max-width: 1024px) {
    display: flex;
  }

  @media (max-width: 394px) {
    flex-shrink: 0;
    flex-wrap: nowrap;
    white-space: nowrap;
  }

  @media (max-width: 440px) {
    margin-left: 0;
  }

  @media (min-width: 440px) {
    margin-left: auto;
  }
`;

const MenuRow = styled.div`
  @media (max-width: 1024px) {
    display: none;
  }
`;

const HeaderBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 398px) {
    flex-wrap: nowrap;
    gap: 0.25rem;

    .col-sm-3 {
      margin-left: -24px !important;
    }

    #new-claim-button {
      width: auto !important;
      display: inline-block !important;
      margin-left: auto !important;
      margin-right: 0 !important;
      text-align: center;
      padding-left: 1.25rem !important;
      padding-left: 1.25rem !important;
    }
  }
`;

const CustomDrawer = styled(Drawer)`
  max-width: 1280px;
`;

export const getAccountSettingsLink = async (setOdyessiUrl: Function) => {
  const remoteConfig = await getSimpleRemoteConfig();
  setOdyessiUrl(remoteConfig['odyssei_preferences_url']);
};

const getDynamicHeaderContent = async (
  setContent: Function,
  personData: any,
  assets: IKenticoData['assets'],
  callCenter: IKenticoData['callCenter'],
) => {
  const result = await getContent(globalThis.location.pathname, 'header');

  registerSelf(Handlebars, () => '', assets);
  setContent(Handlebars.compile(result)({ ...personData, callCenter }));
};

const getShowPaymentInfo = async (
  ERContent: any,
  setShowPaymentInfo: Function,
) => {
  const showInfo = await showPaymentPageBasedOnEmployerId(ERContent);
  setShowPaymentInfo(showInfo);
};

const getEducationalContent = async (
  ERContent: IEmployerContent | undefined,
  setShowEducationTab: Function,
  setEducationTabName: Function,
) => {
  if (
    ERContent?.educationalContent &&
    ERContent.educationalContent?.length > 0
  ) {
    setShowEducationTab(true);
    setEducationTabName(
      (ERContent?.educationalContent[0] as EmployerEducationalContent).elements
        .tab_name?.value ?? 'Employer Benefits',
    );
  }
};

export const userHasDueTasks = (
  personData: PersonData | undefined,
  setHasDueTasks: Function,
  tasks: any,
) => {
  if (personData?.person?.customerNumber) {
    try {
      const openTasks = tasks?.tasks?.some(
        (task: any) => isDueTask(task.taskStatusId) && task.assigneeId === 2,
      );

      setHasDueTasks(openTasks ? 1 : 0);
    } catch (err) {
      console.log(err);
    }
  }
};

const DashboardMenu: (p: DashboardMenuProps) => React.ReactElement = (
  props,
) => {
  const waitForPaint = React.useCallback(
    () =>
      new Promise<void>((resolve) => {
        if (typeof globalThis.requestAnimationFrame === 'function') {
          globalThis.requestAnimationFrame(() => resolve());
          return;
        }

        globalThis.setTimeout(() => resolve(), 0);
      }),
    [],
  );

  const domIds = domIdsUnique(props.keyId);
  const [expanded, setExpanded] = React.useState(false);
  const [odyessiUrl, setOdyessiUrl] = React.useState<string>('');
  const [content, setContent] = React.useState<any>();
  const [showPaymentPrefs, setShowPaymentPrefs] = React.useState<boolean>(true);
  const [showPaymentInfo, setShowPaymentInfo] = React.useState<boolean>(true);
  const { assets, callCenter } = React.useContext(KenticoDataContext);
  const [hasDueTasks, setHasDueTasks] = React.useState<number>(0);
  const [showEducationTab, setShowEducationTab] =
    React.useState<boolean>(false);
  const [educationTabName, setEducationTabName] = React.useState<string>('');
  const { ERContent } = React.useContext(PreferencesContext);
  const [reminderMenuItems, setReminderMenuItems] = React.useState<
    ReminderMenuItem[]
  >([]);
  const [isMobileReminderMenuOpen, setIsMobileReminderMenuOpen] =
    React.useState<boolean>(false);
  const [isLoggingOut, setIsLoggingOut] = React.useState<boolean>(false);
  const [displayToast, setDisplayToast] = React.useState<boolean>(false);
  const [toastMessage, setToastMessage] = React.useState<string>('');
  const overlayRef = React.useRef<HTMLDivElement>(null);
  const previousFocusRef = React.useRef<HTMLElement | null>(null);
  const history = useHistory();

  const accountMenuRoute = React.useCallback(
    (route: any) => history.push(route),
    [history],
  );

  const closeToast = React.useCallback(() => {
    setDisplayToast(false);
  }, []);

  React.useEffect(() => {
    if (!displayToast) {
      return;
    }

    const closeToastTimer = globalThis.setTimeout(() => {
      closeToast();
    }, 8000);

    return () => {
      globalThis.clearTimeout(closeToastTimer);
    };
  }, [displayToast, closeToast]);

  const handleLogout = React.useCallback(async () => {
    if (isLoggingOut) {
      return;
    }

    setIsLoggingOut(true);
    setDisplayToast(false);
    setToastMessage('');

    try {
      await waitForPaint();
      await logout({ strict: true, timeoutMs: logoutTimeout });
    } catch (error) {
      console.error(error);
      setToastMessage(logoutError);
      setDisplayToast(true);
      setIsLoggingOut(false);
    }
  }, [isLoggingOut, waitForPaint]);

  React.useEffect(() => {
    if (!isLoggingOut) {
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
      return;
    }

    previousFocusRef.current = document.activeElement as HTMLElement;
    overlayRef.current?.focus();

    const handleFocusIn = (event: FocusEvent) => {
      const overlayElement = overlayRef.current;

      if (!overlayElement) {
        return;
      }

      if (!overlayElement.contains(event.target as Node)) {
        overlayElement.focus();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        event.preventDefault();
        overlayRef.current?.focus();
      }
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLoggingOut]);

  const { personData } = React.useContext(PersonDataContext);
  const location = useLocation();
  let menuItems: any = [];
  let accountMenuItems: any = [];

  const { data: tasks } = useTasksGet({
    customerNumber: personData?.person?.customerNumber,
  });

  React.useEffect(() => {
    let active = true;
    const unlisten = history.listen(() => {
      if (!content && active) {
        getDynamicHeaderContent(
          (val: any) => {
            if (active) setContent(val);
          },
          personData,
          assets,
          callCenter,
        );
      }
    });
    return () => {
      active = false;
      unlisten();
    };
  }, [history, personData, content, assets, callCenter]);

  React.useEffect(() => {
    let active = true;
    getAccountSettingsLink((val: string) => {
      if (active) setOdyessiUrl(val);
    });
    if (!content) {
      getDynamicHeaderContent(
        (val: any) => {
          if (active) setContent(val);
        },
        personData,
        assets,
        callCenter,
      );
    }
    return () => {
      active = false;
    };
  }, [assets, callCenter, content, personData]);

  React.useEffect(() => {
    userHasDueTasks(personData, setHasDueTasks, tasks);
    setShowPaymentPrefs(personData?.eeCentric?.checkCutting ?? false);
  }, [personData, tasks]);

  React.useEffect(() => {
    getEducationalContent(ERContent, setShowEducationTab, setEducationTabName);
  }, [ERContent]);

  React.useEffect(() => {
    getShowPaymentInfo(ERContent, setShowPaymentInfo);
  }, [ERContent]);

  const { data: reminderData, status: reminderQueryStatus } = useQuery({
    queryKey: ['reminders'],
    queryFn: () => getReminderData(),
    enabled: !!personData?.person?.eid,
  });

  React.useEffect(() => {
    if (reminderData && reminderData.length > 0) {
      setReminderMenuItems(reminderData);
    }
  }, [personData, reminderData]);

  const getAccountMenuItems = () => {
    const accountItems = [
      {
        displayText: 'Account Settings',
        action: () => globalThis.location.assign(odyessiUrl),
      },
      {
        displayText: 'Notification Preferences',
        action: () => accountMenuRoute(AppPaths.profile),
      },
      {
        displayText: paymentPrefs,
        action: () => accountMenuRoute(AppPaths.profile),
      },
      {
        displayText: 'Log out',
        action: () => handleLogout(),
      },
    ];

    if (!showPaymentPrefs) {
      return accountItems.filter((m) => m.displayText !== paymentPrefs);
    }

    return accountItems;
  };

  const onSelect = () => {
    setExpanded(!expanded);
  };

  function GetRoute() {
    if (location.pathname.endsWith('/')) {
      return location.pathname.substring(0, location.pathname.length - 1); //remove trailing forward slash if exists
    }

    return location.pathname;
  }

  if (odyessiUrl) {
    menuItems = getMenuItems(
      odyessiUrl,
      showPaymentPrefs,
      hasDueTasks,
      showPaymentInfo,
      showEducationTab,
      educationTabName,
      handleLogout,
    );
    accountMenuItems = getAccountMenuItems();
  }

  return (
    <>
      <ToastNotificationGroupStyle
        style={{
          top: 0,
          right: 0,
          alignItems: 'flex-end',
          position: 'fixed',
        }}
      >
        {displayToast ? (
          <StyledNotification
            key="logout-error"
            type={{ style: 'error', icon: true }}
            closable={true}
            onClose={closeToast}
          >
            {toastMessage}
          </StyledNotification>
        ) : null}
      </ToastNotificationGroupStyle>
      <CustomDrawer
        expanded={expanded}
        position={'start'}
        mode={'overlay'}
        animation={{
          duration: 400,
        }}
        items={menuItems.map((item: any) => ({
          ...item,
        }))}
        onOverlayClick={() => setExpanded(!expanded)}
        onSelect={onSelect}
        item={CustomDrawerItem}
      >
        <Container id={domIds.rootNode} aria-hidden={isLoggingOut}>
          <MobileReminderMenuComponent
            menuItems={reminderMenuItems}
            queryStatus={reminderQueryStatus}
            setOpenMenu={setIsMobileReminderMenuOpen}
            isOpen={isMobileReminderMenuOpen}
          ></MobileReminderMenuComponent>
          <HeaderBar className="mr-0 row">
            <LogoArea />
            <ButtonContainer>
              <MobileReminderMenu
                menuItems={reminderMenuItems}
                mobileMenuIsOpen={isMobileReminderMenuOpen}
                mobileMenuFunction={setIsMobileReminderMenuOpen}
              ></MobileReminderMenu>
              <MobileMenuButton
                className="col-sm-1"
                onClick={() => setExpanded(!expanded)}
              >
                <img src={MobileMenuIcon} alt="" />
              </MobileMenuButton>
            </ButtonContainer>
            <AccountMenu
              firstName={personData?.person?.firstName}
              lastName={personData?.person?.lastName}
              menuItems={accountMenuItems}
              reminderMenuItems={reminderMenuItems}
              reminderQueryStatus={reminderQueryStatus}
            />
          </HeaderBar>

          <div className="tw-items-center tw-justify-between tw-grid tw-grid-cols-12 tw-gap-3 tw-mt-4">
            <MenuRow className="tw-col-span-12 tw-px-0">
              {menuItems
                ?.filter((m: any) => m.mobileonly === 'N')
                .map((m: any) => (
                  <DashboardMenuItemMod
                    id={domIds.menuItem + m.id}
                    key={domIds.menuItem + m.id + m.route}
                    to={m.route}
                    className="col-sm-auto mg:tw-text-base lg:tw-text-lg first:tw-pl-0 last:tw-pr-0"
                  >
                    <ItemText
                      // because styled components won't allow booleans for non standard attributes
                      active={GetRoute() === m.route ? 1 : 0}
                    >
                      {m.text}
                      {m.statusbubble === 1 && <StatusBubble />}
                    </ItemText>
                  </DashboardMenuItemMod>
                ))}
            </MenuRow>
            <div className="tw-hidden lg:tw-block tw-col-span-9"></div>
            <div className="p-0 lg:tw-block tw-col-span-12 lg:tw-col-span-3 tw-justify-self-end lg:tw-justify-self-auto">
              <Link to={enums.AppPaths.startClaim}>
                <Button
                  className="tw-w-full tw-px-5"
                  buttonStyle={ButtonTypes.primary}
                  id={domIds.newClaimBtn}
                  data-gtm-dl={JSON.stringify({
                    attribute1: 'file a claim',
                    attribute2: 'Start a leave or claim',
                    attribute3: 'claim_interaction',
                    attribute4: 'undefined',
                  })}
                >
                  Start a leave or claim{' '}
                </Button>
              </Link>
            </div>
          </div>
        </Container>
      </CustomDrawer>
      {content
        ? convertAnchorTagsToRouterLinks(getHtmlFromMarkdown(content).__html)
        : null}
      {isLoggingOut ? (
        <div
          className="tw-fixed tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-z-[9999] tw-bg-white"
          ref={overlayRef}
          aria-live="polite"
          aria-label="Signing you out"
          tabIndex={-1}
        >
          <div className="tw-text-unm-primary01-700 tw-text-base">
            Signing you out…
          </div>
          <Spinner />
        </div>
      ) : null}
    </>
  );
};

/*******************************************************************************
 * exported api definition
 ******************************************************************************/
export { DashboardMenu, domIdsUnique };
